import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import {
  IFTable,
  IFText,
  IFsvg,
  IFDialog,
  IFButton,
  IFTooltipIconsLoading,
  ChargingTokenItemDetails,
} from 'Components'
import ChargingTokenSelectors from 'Stores/ChargingToken/Selectors'
import ChargingTokenActions from 'Stores/ChargingToken/Actions'
import { useTranslation } from 'react-i18next'
import RequestState from 'Enums/RequestState'
import Skeleton from '@mui/material/Skeleton'
import { Colors } from 'Theme'
import Styles from './ChargingTokenList.module.css'
import { Tooltip, IconButton, Box, Popover } from '@mui/material'
import { capitalizeFirstLetter, formatDate } from '../../Utils/StringFunctions'
import ReportIcon from '@mui/icons-material/Report'
import EditIcon from '@mui/icons-material/Edit'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrash } from '@fortawesome/free-solid-svg-icons'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import InfinityEnums from 'Enums/InfinityEnums'
import moment from 'moment'
import { Actions, Subjects } from 'Enums/permissionKeys'

const ChargingTokenList = ({
  fetchChargingTokensRequestState,
  chargingTokens,
  tablesHeight,
  paginationOffset,
  fetchChargingTokens,
  loggedInAdmin,
  editingAdminRole,
  allChargingTokenDesigns,
  deleteChargingTokenRequestState,
  deleteChargingToken,
  editChargingToken,
  editChargingTokenRequestState,
  setDeleteChargingTokenRequestState,
  allVouchers,
}) => {
  const [failedRequest, setFailedRequest] = useState(false)
  const [editDetails, setEditDetails] = useState({})
  const [editedDetails, setEditedDetails] = useState({})
  const [activeRowId, setActiveRowId] = useState(null)
  const [selectedChargingToken, setSelectedChargingToken] = useState()
  const deleteModalRef = useRef()
  const tableRef = useRef()
  const childResetRef = useRef(null)
  const contractIdValue = useRef()
  const [table, setTable] = useState()
  const canEditAdmin = editingAdminRole.can(
    Actions.EDIT,
    Subjects.CHARGING_TOKENS,
  )
  const [anchorEl, setAnchorEl] = useState(null)
  const [popoverContent, setPopoverContent] = useState('')
  const { t } = useTranslation()

  const loadMoreData = () => {
    if (
      fetchChargingTokensRequestState === RequestState.LOADING ||
      paginationOffset === null
    )
      return
    fetchChargingTokens(paginationOffset, failedRequest)
  }
  useEffect(() => {
    if (
      fetchChargingTokensRequestState === RequestState.LOADING &&
      tableRef.current
    ) {
      tableRef.current.resetExpandedRows()
    }
  }, [fetchChargingTokensRequestState])
  useEffect(() => {
    if (
      fetchChargingTokensRequestState === RequestState.ERROR_0_NETWORK ||
      fetchChargingTokensRequestState === RequestState.ERROR_400_OCCURRED ||
      fetchChargingTokensRequestState === RequestState.ERROR_401_OCCURRED ||
      fetchChargingTokensRequestState === RequestState.ERROR_403_OCCURRED ||
      fetchChargingTokensRequestState === RequestState.ERROR_409_OCCURRED ||
      fetchChargingTokensRequestState === RequestState.ERROR_UNKNOWN_OCCURRED
    )
      setFailedRequest(true)
    if (fetchChargingTokensRequestState === RequestState.SUCCEEDED)
      setFailedRequest(false)
  }, [fetchChargingTokensRequestState])

  useEffect(() => {
    if (editChargingTokenRequestState === RequestState.SUCCEEDED) {
      setActiveRowId(null)
    }
  }, [editChargingTokenRequestState])

  useEffect(() => {
    if (fetchChargingTokensRequestState === RequestState.LOADING) {
      setActiveRowId(null)
      setEditDetails({})
      table?.setEditingRow(null)
    }
  }, [fetchChargingTokensRequestState])

  const handleRetry = () => {
    fetchChargingTokens(paginationOffset)
  }
  const handleCancel = () => {
    if (childResetRef.current) {
      childResetRef.current()
    }
  }

  const handleEdit = (rowId, table, row) => {
    setTable(table)
    contractIdValue.current = row.original.contractId
    if (activeRowId !== null && activeRowId !== rowId) {
      if (childResetRef.current) {
        childResetRef.current()
      }
      table?.setEditingRow(null)
      setEditDetails((prevState) => ({
        ...prevState,
        [activeRowId]: false,
      }))
    }

    if (activeRowId === rowId) {
      table?.setEditingRow(null)
      setActiveRowId(null)
      setEditDetails((prevState) => ({
        ...prevState,
        [rowId]: false,
      }))
      return
    }

    table?.setEditingRow(row)
    setActiveRowId(rowId)
    setEditDetails((prevState) => ({
      ...prevState,
      [rowId]: true,
    }))

    if (!row.getIsExpanded()) {
      row.toggleExpanded()
      tableRef.current.setExpandedRowExternal(row)
    }
  }

  const handleDetailsChange = (rowId, updatedDetails) => {
    setEditedDetails((prevDetails) => ({
      ...prevDetails,
      [rowId]: updatedDetails,
    }))
  }
  const handleSaveRole = (table, row, values) => {
    const rowId = row.index
    const rowDetails = editedDetails[rowId] || {}

    const updatedValues = {}

    if (values.contractId !== row.original.contractId) {
      updatedValues.contractId = values.contractId
    }
    if (rowDetails.design && rowDetails.design !== row.original.design?._id) {
      updatedValues.design = rowDetails.design
    }
    if (
      rowDetails.reference &&
      rowDetails.reference !== row.original.reference
    ) {
      updatedValues.reference = rowDetails.reference
    }
    if (
      rowDetails.parentId != undefined &&
      rowDetails.parentId !== row.original.parentId
    ) {
      updatedValues.parentId = rowDetails.parentId
    }

    if (
      (!rowDetails.voucherCode && row.original.voucherCode) ||
      (rowDetails.voucherCode &&
        rowDetails.voucherCode !== row.original.voucherCode)
    ) {
      updatedValues.voucherCode = rowDetails.voucherCode
        ? rowDetails.voucherCode
        : ''
    }

    if (Object.keys(updatedValues).length === 0) {
      setActiveRowId(null)
      table?.setEditingRow(null)
      return
    }

    setTable(table)
    editChargingToken(row.original._id, updatedValues)
  }

  const handleSaveRef = useRef(handleSaveRole)
  useEffect(() => {
    handleSaveRef.current = handleSaveRole
  })
  useEffect(() => {
    if (editChargingTokenRequestState === RequestState.SUCCEEDED) {
      table?.setEditingRow(null)
    }
  }, [editChargingTokenRequestState])

  const onDeleteClickHandler = (row, table) => {
    setSelectedChargingToken(row)
    if (activeRowId === row.index) {
      setActiveRowId(null)
      table?.setEditingRow(null)
    }

    deleteModalRef.current.show()
  }

  const handleDeleteChargingToken = () => {
    if (selectedChargingToken.original) {
      deleteChargingToken(selectedChargingToken.original._id)
    }
  }
  useEffect(() => {
    if (deleteChargingTokenRequestState === RequestState.SUCCEEDED) {
      deleteModalRef.current.dismiss()
      setDeleteChargingTokenRequestState(RequestState.UNINITIALIZED)
    }
  }, [deleteChargingTokenRequestState, activeRowId, selectedChargingToken])

  const handlePopoverOpen = (event, row) => {
    const { contractId } = row.original
    const textOverFlow = event.currentTarget.querySelector(
      `.${Styles.TableText}`,
    )
    const isOverflowing = (element) =>
      element && element.scrollWidth > element.clientWidth

    if (isOverflowing(textOverFlow)) {
      setAnchorEl(event.currentTarget)
      setPopoverContent({ contractId })
    }
  }

  const handlePopoverClose = () => {
    setAnchorEl(null)
  }

  const open = Boolean(anchorEl)

  const chargingTokenTableColumns = useMemo(
    () => [
      {
        accessorKey: 'huid',
        enableEditing: false,
        header:
          fetchChargingTokensRequestState === RequestState.LOADING ? (
            <Skeleton
              variant="text"
              width="110px"
              animation="none"
              height={20}
              style={{ Left: '40px' }}
            />
          ) : (
            t('ChargingTokenPage.Huid')
          ),
        Cell: ({ row }) => {
          return row.index < chargingTokens.length ? (
            <div className={Styles.ColumnTitle}>
              <ExpandMoreIcon
                className={Styles.ExpandMoreIcon}
                style={{
                  transform: row.getIsExpanded()
                    ? 'rotate(0deg)'
                    : 'rotate(-90deg)',
                }}
              />
              <div
                className={Styles.ColumnTitle}
                onMouseEnter={(e) => handlePopoverOpen(e, row)}
                onMouseLeave={handlePopoverClose}
              >
                {row.original.huid ? (
                  <>
                    <IFText className={Styles.TableText}>
                      {capitalizeFirstLetter(row.original.huid)}
                    </IFText>
                  </>
                ) : (
                  <IFText>{t('ChargingTokenPage.NotSet')}</IFText>
                )}
              </div>
            </div>
          ) : failedRequest ? (
            <div className={Styles.IconDiv}>
              <ReportIcon
                width={28}
                height={28}
                style={{ color: Colors.red }}
              />
              <IFText>{t('IFTable.TryAgainText')}</IFText>
            </div>
          ) : (
            <Skeleton variant="text" width="60%" animation="none" height={20} />
          )
        },
      },
      {
        accessorKey: 'contractId',
        muiEditTextFieldProps: () => {
          return {
            type: 'text',
            value: contractIdValue?.current,
            onChange: (event) => {
              contractIdValue.current = event.target.value
            },
            sx: {
              '& .MuiInputBase-root:hover::before': {
                borderBottom: `1px solid ${Colors.primary} !important`,
              },
              '& .MuiInputBase-root.Mui-focused::after': {
                borderBottom: `2px solid ${Colors.primary} !important`,
              },
              '& .MuiInputBase-input': {
                fontSize: '14px',
              },
            },
          }
        },
        header:
          fetchChargingTokensRequestState === RequestState.LOADING ? (
            <Skeleton
              variant="text"
              width="55px"
              animation="none"
              height={20}
              style={{ Left: '40px' }}
            />
          ) : (
            t('ChargingTokenPage.ContractID')
          ),
        Cell: ({ row }) => {
          return row.index < chargingTokens.length ? (
            row.original.contractId ? (
              <div
                onMouseEnter={(e) => handlePopoverOpen(e, row)}
                onMouseLeave={handlePopoverClose}
              >
                <IFText className={Styles.TableText}>
                  {capitalizeFirstLetter(row.original.contractId)}
                </IFText>
              </div>
            ) : (
              <IFText>{t('ChargingTokenPage.NotSet')}</IFText>
            )
          ) : failedRequest ? (
            <></>
          ) : (
            <Skeleton variant="text" width="60%" animation="none" height={20} />
          )
        },
      },
      {
        accessorKey: 'type',
        enableEditing: false,
        header:
          fetchChargingTokensRequestState === RequestState.LOADING ? (
            <Skeleton
              variant="text"
              width="110px"
              animation="none"
              height={20}
            />
          ) : (
            <IFText>{t('ChargingTokenPage.Type')}</IFText>
          ),
        Cell: ({ row }) => {
          return row.index < chargingTokens.length ? (
            row.original.type ? (
              <IFText>{capitalizeFirstLetter(row.original.type)}</IFText>
            ) : (
              <IFText>{t('ChargingTokenPage.NotSet')}</IFText>
            )
          ) : failedRequest ? (
            <></>
          ) : (
            <Skeleton variant="text" width="60%" animation="none" height={20} />
          )
        },
      },
      {
        accessorKey: 'status',
        enableEditing: false,
        header:
          fetchChargingTokensRequestState === RequestState.LOADING ? (
            <Skeleton
              variant="text"
              width="110px"
              animation="none"
              height={20}
            />
          ) : (
            <IFText>{t('ChargingTokenPage.Status')}</IFText>
          ),
        Cell: ({ row }) => {
          return row.index < chargingTokens.length ? (
            row.original.status ? (
              <IFText>{capitalizeFirstLetter(row.original.status)}</IFText>
            ) : (
              <IFText>{t('ChargingTokenPage.NotSet')}</IFText>
            )
          ) : failedRequest ? (
            <></>
          ) : (
            <Skeleton variant="text" width="60%" animation="none" height={20} />
          )
        },
      },
      {
        accessorKey: 'createdOn',
        enableEditing: false,
        header:
          fetchChargingTokensRequestState === RequestState.LOADING ? (
            <Skeleton
              variant="text"
              width="110px"
              animation="none"
              height={20}
              style={{ Left: '40px' }}
            />
          ) : (
            t('ChargingTokenPage.CreatedOn')
          ),
        Cell: ({ row }) => {
          return row.index < chargingTokens.length ? (
            <IFText>
              {moment(row.original.createdAt).format('MMM DD, YYYY LTS')}
            </IFText>
          ) : failedRequest ? (
            <></>
          ) : (
            <Skeleton variant="text" width="60%" animation="none" height={20} />
          )
        },
      },
    ],
    [t, fetchChargingTokensRequestState, failedRequest, chargingTokens],
  )
  const fetchMoreOnBottomReached = useCallback(
    (event) => {
      if (event) {
        const { scrollHeight, scrollTop, clientHeight } = event
        if (scrollHeight - scrollTop - clientHeight < 400) {
          loadMoreData()
        }
      }
    },
    [loadMoreData],
  )
  const renderRowActions = ({ table, row }) => {
    const isCurrentAdmin = row.original.id === loggedInAdmin.id
    const canBeEdit =
      row.original.status === InfinityEnums.ChargingTokenStatus.INACTIVE
    const canDelete = row.original.owner === null || !row.original.owner
    const rowId = row.original.id || row.index
    const isInEditMode = activeRowId === rowId

    if (Object.keys(row.original).length > 0 && !isCurrentAdmin) {
      return (
        <Box>
          {isInEditMode && row?.id === table.getState().editingRow?.id ? (
            <div className={Styles.RowActions}>
              {canDelete ? (
                <Tooltip title={t('AdminList.deleteModalConfirm')}>
                  <IconButton
                    onClick={(e) => {
                      onDeleteClickHandler(row, table)
                    }}
                  >
                    <div
                      className={Styles.DeleteIcon}
                      style={{ color: Colors.AdminListDeleteIcon }}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </div>
                  </IconButton>
                </Tooltip>
              ) : null}
              <Tooltip title={t('AdminList.Save')}>
                <div className={Styles.CustomBackGround}>
                  <IFTooltipIconsLoading
                    isLoading={
                      editChargingTokenRequestState === RequestState.LOADING
                        ? true
                        : false
                    }
                    Icon={IFsvg.DoneBtn}
                    FilledIcon={IFsvg.DoneBtn}
                    size={26}
                    animationDisabled={false}
                    onClick={() => {
                      const values = row.getAllCells().reduce((acc, cell) => {
                        acc[cell.column.id] = cell.getValue()
                        return acc
                      }, {})
                      handleSaveRef.current(table, row, values)
                    }}
                  />
                </div>
              </Tooltip>

              <Tooltip title={t('AdminList.deleteModalCancel')}>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation()
                    handleEdit(rowId, table, row)
                    table?.setEditingRow(null)
                    handleCancel()
                  }}
                >
                  <IFsvg.CloseBtn
                    height={24}
                    width={24}
                    fill={Colors.primary}
                  />
                </IconButton>
              </Tooltip>
            </div>
          ) : (
            canBeEdit && (
              <Box className={Styles.RowActions}>
                <Tooltip title={t('AdminList.Edit')}>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation()
                      handleEdit(rowId, table, row)
                    }}
                  >
                    <EditIcon sx={{ color: Colors.gray }} />
                  </IconButton>
                </Tooltip>
              </Box>
            )
          )}
        </Box>
      )
    }

    if (failedRequest) {
      return (
        <IFButton
          size="sm"
          color={Colors.red}
          text={t('IFTable.TryAgain')}
          onClick={handleRetry}
        />
      )
    }

    return null
  }

  const renderDetailPanel = ({ row }) => {
    const rowId = row.original.id || row.index

    return (
      <ChargingTokenItemDetails
        row={row}
        editingAdminRole={editingAdminRole}
        editDetails={activeRowId === rowId}
        allChargingTokenDesigns={allChargingTokenDesigns}
        onDetailsChange={handleDetailsChange}
        onCancelEdit={(resetFn) => {
          if (activeRowId === rowId) {
            childResetRef.current = resetFn
          }
        }}
        chargingTokens={chargingTokens}
        allVouchers={allVouchers}
      />
    )
  }

  return (
    <>
      <IFTable
        ref={tableRef}
        columns={chargingTokenTableColumns}
        data={chargingTokens}
        tableMaxHeight={tablesHeight}
        paginationOffset={paginationOffset}
        fetchMoreOnBottomReached={fetchMoreOnBottomReached}
        tableSetHeight={'100%'}
        showSkeleton={
          fetchChargingTokensRequestState === RequestState.LOADING ||
          (fetchChargingTokensRequestState !== RequestState.SUCCEEDED &&
            paginationOffset === 0)
        }
        tableRequestState={fetchChargingTokensRequestState}
        enableEditing={canEditAdmin}
        renderRowActions={canEditAdmin && renderRowActions}
        renderDetailPanel={renderDetailPanel}
      />
      <Popover
        id="mouse-over-popover"
        className={Styles.Popover}
        open={open}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        disableRestoreFocus
      >
        <Box
          className={Styles.ParentBox}
          sx={{
            p: 2,
          }}
        >
          <Box className={Styles.ChildBox}>
            <IFText style={{ color: Colors.DetailPanel }}>
              <strong>{t('ChargingTokenPage.HuidTitle')}</strong>
            </IFText>
            <IFText
              className={Styles.PopoverText}
              style={{
                color: Colors.text,
              }}
            >
              {popoverContent.huid}
            </IFText>
          </Box>

          <Box className={Styles.SecondChildBox}>
            <IFText style={{ color: Colors.DetailPanel }}>
              <strong>{t('ChargingTokenPage.ContractIdTitle')}</strong>
            </IFText>
            <IFText
              className={Styles.PopoverText}
              style={{
                color: Colors.text,
              }}
            >
              {popoverContent.contractId}
            </IFText>
          </Box>
        </Box>
      </Popover>

      <IFDialog
        ref={deleteModalRef}
        style={{ margin: '15em' }}
        open={false}
        maxWidth={'xs'}
        fullWidth={true}
        title={t('ChargingTokenPage.deleteModalTitle')}
        bodyText={t('ChargingTokenPage.deleteModalTextToken')}
        buttonAcceptonClick={handleDeleteChargingToken}
        buttonCancelColor={Colors.AdminListDeleteModalCancel}
        buttonAcceptColor={Colors.red}
        buttonAcceptText={t('ChargingTokenPage.deleteModalConfirm')}
        buttonClassName={Styles.Button}
        isLoading={deleteChargingTokenRequestState === RequestState.LOADING}
        dismissOnAccept={false}
      />
    </>
  )
}
function mapDispatchToProps(dispatch) {
  return {
    fetchChargingTokens: (offset, shouldShowError) =>
      dispatch(
        ChargingTokenActions.fetchChargingTokens(offset, shouldShowError),
      ),
    deleteChargingToken: (chargingTokenId) =>
      dispatch(ChargingTokenActions.deleteChargingToken(chargingTokenId)),
    editChargingToken: (chargingTokenId, updatedValues) =>
      dispatch(
        ChargingTokenActions.editChargingToken(chargingTokenId, updatedValues),
      ),
    setDeleteChargingTokenRequestState: (newState) =>
      dispatch(
        ChargingTokenActions.setDeleteChargingTokenRequestState(newState),
      ),
  }
}

const mapStateToProps = (state) => ({
  fetchChargingTokensRequestState:
    ChargingTokenSelectors.getFetchChargingTokensRequestState(state),

  paginationOffset: ChargingTokenSelectors.getPaginationOffset(state),

  deleteChargingTokenRequestState:
    ChargingTokenSelectors.getDeleteChargingTokenRequestState(state),
  editChargingTokenRequestState:
    ChargingTokenSelectors.getEditChargingTokenRequestState(state),
})

ChargingTokenList.propTypes = {
  fetchChargingTokensRequestState: PropTypes.number,
  chargingTokens: PropTypes.array,
  tablesHeight: PropTypes.number,
  paginationOffset: PropTypes.number,
  fetchChargingTokens: PropTypes.func,
  loggedInAdmin: PropTypes.object,
  editingAdminRole: PropTypes.object,
  allChargingTokenDesigns: PropTypes.object,
  deleteChargingTokenRequestState: PropTypes.number,
  deleteChargingToken: PropTypes.func,
  editChargingToken: PropTypes.func,
  editChargingTokenRequestState: PropTypes.number,
  setDeleteChargingTokenRequestState: PropTypes.func,
}

export default connect(mapStateToProps, mapDispatchToProps)(ChargingTokenList)
